import {
    ModalVariant,
    OperatorRoutedModal,
} from '../ui/modal/OperatorRoutedModal.tsx';
import { Box, Button, TextField } from '@mui/material';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { useState } from 'react';
import { SubmitButton } from '../ui/buttons/SubmitButton.tsx';
import { Outlet, useNavigate, useParams } from 'react-router';
import {
    RequestStatus,
    useServerErrorEffect,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { setAppToken } from '../app/components/Native.tsx';
import * as AuthenticationState from '../app/state/AuthenticationState.ts';
import { AuthStatusType } from '../app/state/AuthenticationState.ts';
import * as LandingPageState from './LandingPageState.ts';
import { useUpdate } from 'dg-web-shared/lib/Flux.tsx';
import { LoginOtpStatus } from '../app/actions/AuthenticationActions.ts';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from '../operator-logins/components/ConfirmationDialog.tsx';
import { useIsMobileRoute } from '../layout/components/BreadCrumb.tsx';
import { ParkingPortalLayoutWithHeader } from '../mobile/layout/ParkingPortalLayoutWithHeader.tsx';

interface AuthTokenResponse {
    appToken: string;
    otpStatus: LoginOtpStatus;
}

interface UnauthorizedOtpResponse {
    reason: UnauthorizedReason;
}

enum UnauthorizedReason {
    LOGIN_REQUIRED = 'LOGIN_REQUIRED',
    OTP_INVALID = 'OTP_INVALID',
}

export function OtpCheck() {
    const navigate = useNavigate();
    const update = useUpdate();
    const [otpToken, setOtpToken] = useState<string>('');

    const [otpTokenState, submitOtpToken] = useServerWrite<
        { token: string },
        AuthTokenResponse,
        UnauthorizedOtpResponse
    >(_ => ({
        url: `/ui-api/operator-account/auth-otp`,
    }));

    const isMobile = useIsMobileRoute();

    useServerSuccessEffect(otpTokenState, data => {
        update(store => {
            setAppToken(data.appToken);
            const authenticationState = new AuthenticationState.StateSlice(
                store,
            );
            authenticationState.writeStatus(AuthStatusType.Authorized);
            LandingPageState.LoginState.reset(store);
            return 'state-management-on-authorize-success';
        });
    });

    useServerErrorEffect(otpTokenState, (_, data) =>
        navigate(`error/${data?.reason}`),
    );

    return (
        <OperatorRoutedModal
            variant={isMobile ? ModalVariant.MOBILE : ModalVariant.TINY}
            backUrl={'..'}
            render={() => (
                <>
                    <ParkingPortalLayoutWithHeader
                        title={
                            <Localized
                                de="Zwei-Faktor-Authentifizierung"
                                fr="Authentification à deux facteurs"
                                it="Autenticazione a due fattori"
                                en="Two-Factor Authentication"
                            />
                        }
                        backTo={'..'}
                    >
                        <Box
                            sx={{
                                padding: theme => theme.spacing(3),
                                display: 'flex',
                                flex: 1,
                                flexDirection: 'column',
                                color: theme => theme.palette.primary.main,
                            }}
                        >
                            <Box
                                sx={{
                                    marginBottom: theme => theme.spacing(2),
                                }}
                            >
                                <Localized
                                    de="Du hast die Zwei-Faktor-Authentifizierung aktiviert. Gib den Code aus deiner Authentifizierungs-App ein."
                                    fr="Vous avez activé l'authentification à deux facteurs. Entrez le code de votre application d'authentification."
                                    it="Hai attivato l'autenticazione a due fattori. Inserisci il codice dalla tua app di autenticazione."
                                    en="You have two-factor authentication enabled. Please enter the code from your authenticator app."
                                />
                            </Box>
                            <TextField
                                autoComplete="one-time-code"
                                label={
                                    <Localized
                                        de="Einmalcode"
                                        fr="Code à usage unique"
                                        it="Codice monouso"
                                        en="One Time Code"
                                    />
                                }
                                value={otpToken}
                                onChange={v => {
                                    const value = v.target.value;
                                    if (/^\d{0,6}$/.test(value)) {
                                        setOtpToken(value);
                                    }
                                }}
                                slotProps={{
                                    htmlInput: {
                                        inputMode: 'numeric',
                                        maxLength: 6,
                                    },
                                }}
                            />
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    marginTop: theme => theme.spacing(5),
                                }}
                            >
                                <Button
                                    onClick={() => navigate('..')}
                                    variant="outlined"
                                    color={'primary'}
                                    sx={{
                                        marginRight: theme => theme.spacing(1),
                                    }}
                                >
                                    <Localized
                                        de="Abbrechen"
                                        fr="Annuler"
                                        it="Annulla"
                                        en="Cancel"
                                    />
                                </Button>
                                <SubmitButton
                                    loading={
                                        otpTokenState.status ===
                                        RequestStatus.PENDING
                                    }
                                    onClick={() =>
                                        submitOtpToken({ token: otpToken })
                                    }
                                    label={
                                        <Localized
                                            de="Bestätigen"
                                            fr="Confirmer"
                                            it="Conferma"
                                            en="Submit"
                                        />
                                    }
                                    disabled={otpToken === ''}
                                />
                            </Box>
                        </Box>
                    </ParkingPortalLayoutWithHeader>

                    <Outlet />
                </>
            )}
        />
    );
}

function isUnauthorizedReason(value?: string): value is UnauthorizedReason {
    return Object.values(UnauthorizedReason).includes(
        value as UnauthorizedReason,
    );
}

export function getUnauthorizedReason(
    value?: string,
): UnauthorizedReason | null {
    if (isUnauthorizedReason(value)) {
        return value;
    }
    return null;
}

export function OtpErrorModal() {
    const navigate = useNavigate();
    const urlParams = useParams<{ reason: string }>();
    const reason = getUnauthorizedReason(urlParams.reason);

    switch (reason) {
        case UnauthorizedReason.LOGIN_REQUIRED:
            return (
                <ConfirmationDialog
                    open
                    title={
                        <Localized
                            de="Authentifizierung erforderlich"
                            fr="Authentification requise"
                            it="Autenticazione richiesta"
                            en="Authentication Required"
                        />
                    }
                    confirmLabel={<Localized de="OK" fr="OK" it="OK" en="OK" />}
                    onConfirm={() => {
                        navigate('/');
                    }}
                    dialogVersion={ConfirmationDialogVersions.ERROR}
                >
                    <p>
                        <Localized
                            de="Bitte melden Sie sich erneut an, um fortzufahren."
                            fr="Veuillez vous reconnecter pour continuer."
                            it="Accedi nuovamente per continuare."
                            en="Please log in again to continue."
                        />
                    </p>
                </ConfirmationDialog>
            );
        case UnauthorizedReason.OTP_INVALID:
            return (
                <ConfirmationDialog
                    open
                    title={
                        <Localized
                            de="Code ist ungültig"
                            fr="Code pas valide"
                            it="Codice non è valido"
                            en="Invalid code"
                        />
                    }
                    confirmLabel={<Localized de="OK" fr="OK" it="OK" en="OK" />}
                    onConfirm={() => navigate('..')}
                    dialogVersion={ConfirmationDialogVersions.ERROR}
                >
                    <p>
                        <Localized
                            de="Der eingegebene Code ist ungültig."
                            fr="Le code saisi n'est pas valide."
                            it="Il codice inserito non è valido."
                            en="The entered code is invalid."
                        />
                        <Localized
                            de="Bitte überprüfen Sie Ihre Eingabe und versuchen Sie es erneut."
                            fr="Veuillez vérifier votre saisie et réessayer."
                            it="Controlla il codice inserito e riprova."
                            en="Please check your input and try again."
                        />
                    </p>
                </ConfirmationDialog>
            );
        case null:
            return (
                <ConfirmationDialog
                    open
                    title={
                        <Localized
                            de="Fehler"
                            fr="Erreur"
                            en="Error"
                            it="Errore"
                        />
                    }
                    confirmLabel={<Localized de="OK" fr="OK" it="OK" en="OK" />}
                    onConfirm={() => navigate('..')}
                    dialogVersion={ConfirmationDialogVersions.ERROR}
                >
                    <p>
                        <Localized
                            de="Ein unerwarteter Fehler ist aufgetreten."
                            fr="Une erreur inattendue s'est produite."
                            it="Si è verificato un errore imprevisto."
                            en="An unexpected error has occurred."
                        />
                    </p>
                </ConfirmationDialog>
            );
    }
}
